import { useIntersectionObserver, useThrottledCallback } from "@react-hookz/web";
import { useCallback, useEffect, useState } from "react";

import { sendGATracking } from "./apis";
import type { Event } from "./types";

export const useTrackImpressionEventByIntersection = <T extends HTMLElement>(
  event: (() => Event) | null,
  options?: {
    intersectionThreshold?: number[];
    shouldSendOnce?: boolean;
    shouldThrottle?: boolean;
  },
) => {
  const [targetElement, setTargetElement] = useState<T | null>(null);
  const [hasSent, setHasSent] = useState(false);

  const shouldSend = options?.shouldSendOnce ? !hasSent : true;
  const intersection = useIntersectionObserver(shouldSend && event ? targetElement : null, {
    threshold: options?.intersectionThreshold,
  });

  const handleSend = useThrottledCallback(
    (event: Event) => {
      sendGATracking(event);
      setHasSent(true);
    },
    [sendGATracking, setHasSent],
    options?.shouldThrottle ? 300 : 0,
    true,
  );

  useEffect(() => {
    if (!shouldSend || !intersection?.isIntersecting || !event) return;
    handleSend(event());
  }, [event, shouldSend, intersection?.isIntersecting, handleSend]);

  useEffect(() => {
    setHasSent(false);
  }, [targetElement]);

  const captureTrackImpressionEventTargetElement = useCallback(
    (element: T | null) => {
      setTargetElement(element);
    },
    [setTargetElement],
  );

  return { captureTrackImpressionEventTargetElement };
};
